import { CharObject } from "../objects/charobject";
import { MyCharacter } from "../objects/mycharacter";
import { CharPawn, getPatVehicleKind } from "../pawns/charpawn";
import { CharStats } from "../gamedata/charstats";
import { NpcObject } from "../objects/npcobject";
import { MobObject } from "../objects/mobobject";
import { NpcStats } from "../gamedata/npcstats";
import { NpcPawn } from "../pawns/npcpawn";
import { ItemObject } from "../objects/itemobject";
import { ItemPawn } from "../pawns/itempawn";
import { ProxyObject } from "../objects/proxyobject";
import { IngStatus } from "../gamedata/ingstatus";
import { attachBuffEffect } from "../effects/effectmanager";
import { GCM } from "./chatmanager";
import { GDM } from "./datamanager";
import { computeNpcHasAvailableQuest } from "../gamedata/npcquestmarker";
import {
    INVEQUIPIDX,
    ITEMLOC,
    ITEMTYPE,
    VISEQUIPIDX,
    WEAPON_DATA,
} from "../gamedata/inventorydata";
import { playHitSound, playSoundByIdx } from "./soundmanager";
import {
    RESULT_QUEST_REPLY_ADD_FAILED,
    RESULT_QUEST_REPLY_ADD_SUCCESS,
    RESULT_QUEST_REPLY_COMPLETE,
    RESULT_QUEST_REPLY_DAILY_RESET,
    RESULT_QUEST_REPLY_DEL_FAILED,
    RESULT_QUEST_REPLY_DEL_SUCCESS,
    RESULT_QUEST_REPLY_RESET,
    RESULT_QUEST_REPLY_TRIGGER_FAILED,
    RESULT_QUEST_REPLY_TRIGGER_SUCCESS,
    RESULT_QUEST_REPLY_UPDATE,
    RESULT_QUEST_REWARD_ADD_ITEM,
    RESULT_QUEST_REWARD_REMOVE_ITEM,
} from "../net/networkconstants";

global.OBJECT_COMMAND = {
    STOP: 0,
    ATTACK: 1,
    SIT: 2,
    STAND: 3,
    MOVE: 4,
    DIE: 5,
    TOGGLE: 6,
    Skill2SELF: 7,
    Skill2OBJ: 8,
    Skill2POS: 9,
    MAX: 10,
};

/**
 * This class handles tying together the networking clients with the
 * actual game world and GameObjectManager.
 *
 * @private
 */
class _NetManager {
    world: any;
    pendingNpcLoads: number;
    npcLoadCallbacks: (() => void)[];
    isInitialLoad: boolean;
    vendingPropIds: Map<number, number>;
    pendingMobSpawns: Map<number, number>;
    pendingMobAttacks: Map<number, any>;
    nextMobSpawnToken: number;

    constructor() {
        this.world = null;
        this.pendingNpcLoads = 0;
        this.npcLoadCallbacks = [];
        this.isInitialLoad = false;
        this.vendingPropIds = new Map();
        this.pendingMobSpawns = new Map();
        this.pendingMobAttacks = new Map();
        this.nextMobSpawnToken = 0;
    }

    verifyMapRevision(serverRevision: unknown): boolean {
        const clientRevision = this.world?.compiledTerrain?.revision ?? "";
        const expected = typeof serverRevision === "string" ? serverRevision : "";
        if (clientRevision === expected) return true;
        const message =
            `Map package revision mismatch: client=${clientRevision || "legacy"}, ` +
            `server=${expected || "legacy/database"}. Reload or switch both runtimes ` +
            "to the same map source.";
        console.error(message);
        if (typeof window !== "undefined" && typeof window.alert === "function") {
            window.alert(message);
        }
        return false;
    }

    private playMonsterAttack(mob: MobObject, data: any) {
        const target = GZM.findByServerObjectIdx(data.defenderObjectIdx);
        const targetPosition = target?.position || data.posTo;
        mob.setDirection(Math.atan2(
            targetPosition.y - mob.position.y,
            targetPosition.x - mob.position.x
        ) + Math.PI / 2);
        mob.pawn?.playAttackMotion(() => {
            if (!mob.isDead) mob.pawn?.playIdleMotion();
        });
    }

    private playRegularHitSound(attacker: any) {
        if (attacker instanceof MobObject) {
            playHitSound(attacker.stats?.getAttackSound?.());
            return;
        }

        if (!(attacker instanceof CharObject)) return;
        const weapon = attacker instanceof MyCharacter
            ? attacker.inventory?.findByLocSlot?.(ITEMLOC.EQUIPPED_EQUIP, INVEQUIPIDX.WEAPON)
            : attacker.visParts?.[VISEQUIPIDX.WEAPON];
        if (!weapon?.itemNo) {
            // LIST_HITSOUND row 1 is the bare-hands impact group.
            playHitSound(1);
            return;
        }

        GDM.get("item_data", (itemData: any) => {
            const weaponData = itemData.getData(ITEMTYPE.WEAPON, weapon.itemNo);
            const hitSound = Number(weaponData?.[WEAPON_DATA.HIT_SOUND]);
            if (hitSound > 0) {
                playHitSound(hitSound);
            } else {
                // Ranged/magic weapons commonly have no impact group, but do
                // carry a direct shooting/start FILE_SOUND index.
                playSoundByIdx(
                    weaponData?.[WEAPON_DATA.SHOT_SOUND] ||
                    weaponData?.[WEAPON_DATA.START_SOUND]
                );
            }
        });
    }

    /**
     * @private
     */
    _destroyWorld() {
        throw new Error("Not Yet Supported");
    }

    /**
     * Start tracking initial NPC loads
     */
    startInitialLoad() {
        console.log("[NetManager] Starting initial load tracking");
        this.isInitialLoad = true;
        this.pendingNpcLoads = 0;
        this.npcLoadCallbacks = [];
    }

    /**
     * Stop tracking initial loads
     * Note: Don't notify here - let waitForNpcLoads() handle waiting
     */
    finishInitialLoad() {
        console.log("[NetManager] Finishing initial load, pending:", this.pendingNpcLoads);
        this.isInitialLoad = false;
        // If NPCs have already finished loading and someone is waiting, notify them
        if (this.pendingNpcLoads === 0 && this.npcLoadCallbacks.length > 0) {
            this._notifyNpcLoadComplete();
        }
    }

    /**
     * Wait for all pending NPC loads to complete
     */
    async waitForNpcLoads(): Promise<void> {
        if (this.pendingNpcLoads === 0) {
            console.log("[NetManager] No pending NPC loads");
            return Promise.resolve();
        }
        console.log("[NetManager] Waiting for", this.pendingNpcLoads, "NPCs to load");
        return new Promise<void>((resolve) => {
            this.npcLoadCallbacks.push(resolve);
        });
    }

    /**
     * Called when an NPC finishes loading
     */
    _onNpcLoadComplete() {
        this.pendingNpcLoads--;
        console.log("[NetManager] NPC loaded, pending:", this.pendingNpcLoads);
        if (this.pendingNpcLoads === 0 && !this.isInitialLoad) {
            this._notifyNpcLoadComplete();
        }
    }

    /**
     * Notify all waiting callbacks that NPC loading is complete
     */
    _notifyNpcLoadComplete() {
        if (this.npcLoadCallbacks.length === 0) {
            console.log("[NetManager] NPCs loaded but no callbacks waiting");
            return;
        }
        console.log("[NetManager] All NPCs loaded, notifying", this.npcLoadCallbacks.length, "callbacks");
        const callbacks = this.npcLoadCallbacks;
        this.npcLoadCallbacks = [];
        callbacks.forEach(cb => cb());
    }

    /**
     * Begins watching a WorldClient and GameClient for events.
     *
     * Note: You must set the world property before calling this!
     *
     * @param {WorldClient} wn
     * @param {GameClient} gn
     */
    watch(wn: any, gn: any) {
        gn.on("welcome_msg", (data: any) => {
            GCM.system(data.message);
        });
        wn.on("end", () => {
            gn.end();
            this._destroyWorld();
        });
        gn.on("end", () => {
            wn.end();
            this._destroyWorld();
        });

        gn.on("inventory_data", (data: any) => {
            MC.inventory.setMoney(data.money);
            if (data.result === 0x1) {
                MC.inventory.setItems(data.items);
            } else if (data.result === 0x2) {
                MC.inventory.appendItems(data.items);
            } else {
                console.warn(
                    "Received unexpected inventory_data result.",
                    data.result
                );
            }
            if (MC.pawn instanceof CharPawn) {
                const patItems = [135, 136, 137, 138, 139].map((slot) => MC.inventory.findBySlot(slot)?.itemNo || 0);
                if (patItems[0]) {
                    // item_data may not have finished loading yet: this packet can
                    // arrive before GameState's "Loading game data..." task
                    // resolves, since NetManager.watch is wired up in PreGameState.
                    GDM.get('item_data', (itemData: any) => {
                        const vehicleKind = getPatVehicleKind(itemData.getPatStats(patItems[0])?.subtype);
                        CharPawn.preloadPatParts(patItems, vehicleKind);
                        if (MC.isDriving) {
                            MC.pawn.setPatParts(patItems, vehicleKind);
                            MC.pawn.setDrivingHeight(true, vehicleKind);
                        }
                    });
                }
            }
        });

        gn.on("skill_data", (data: any) => {
            if (data.result === 0x1) {
                MC.skills.setSkills(data.skills);
            } else if (data.result === 0x2) {
                MC.skills.appendSkills(data.skills);
            } else {
                console.warn(
                    "Received unexpected skill_data result.",
                    data.result
                );
            }
        });

        gn.on("inventory_change_items", (data: any) => {
            MC.inventory.changeItems(data.changeItems);
        });

        gn.on("quest_log", (data: any) => {
            MC.quests.setQuests(data.quests);
        });
        gn.on("quest_vars", (data: any) => {
            MC.quests.setVars(data.vars);
        });
        gn.on("questitem_list", (data: any) => {
            MC.quests.setItems(data.items);
        });
        gn.on("questitem_reward", (data: any) => {
            const itemData = GDM.getNow("item_data");
            const item = data.questItem.item;
            const name = itemData.getName(item.itemType, item.itemNo);

            // We don't actually update MC.quests here because server sends questitem_list anyway.
            if (data.result === RESULT_QUEST_REWARD_ADD_ITEM) {
                GCM.questReward(
                    "You have earned " + name + " (" + item.quantity + ")."
                );
            } else if (data.result === RESULT_QUEST_REWARD_REMOVE_ITEM) {
                GCM.questReward(
                    "You have lost " + name + " (" + item.quantity + ")."
                );
            }
        });
        gn.on("quest_completion_data", (data: any) => {
            MC.quests.setDailyLog(data.dailyLog);
        });
        MC.quests.on("changed", () => {
            for (const obj of GZM.objects.objects) {
                if (obj.type === "npc" && obj.pawn) {
                    computeNpcHasAvailableQuest(obj).then((hasQuest: boolean) => obj.pawn?.setQuestMarker(hasQuest));
                }
            }
        });

        const NETOBJCMD = {
            STOP: 0x0000,
            MOVE: 0x0001,
            ATTACK: 0x0002,
            DIE: 0x0003,
            PICKITEM: 0x0004,
            SKILL2SELF: 0x0006,
            SKILL2OBJ: 0x0007,
            SKILL2POS: 0x0008,
            RUNAWAY: 0x8009,
            SIT: 0x000a,
            STORE: 0x000b,
        };

        function _setDead(go: any) {
            if (go.isDead) return;
            go.emit("died");
            go._die(() => {
                if (go instanceof MobObject && !(go instanceof NpcObject)) {
                    GZM.removeObject(go);
                }
            });
        }

        function _setCommand(go: any, data: any) {
            switch (data.command) {
                case NETOBJCMD.DIE:
                    _setDead(go);
                    break;
                case NETOBJCMD.SIT:
                    if (go instanceof CharObject) {
                        go._sit();
                    } else {
                        go._stop();
                    }
                    break;
                case NETOBJCMD.STOP:
                    go._stop();
                    break;
                case NETOBJCMD.ATTACK:
                    let targetObj = GZM.getRefByServerObjectIdx(
                        data.targetObj,
                        new THREE.Vector3(data.posTo.x, data.posTo.y, 0)
                    );
                    go._attackObj(targetObj);
                    break;
                case NETOBJCMD.RUNAWAY:
                case NETOBJCMD.MOVE:
                case NETOBJCMD.PICKITEM:
                    if (data.targetObj) {
                        let targetObj = GZM.getRefByServerObjectIdx(
                            data.targetObj,
                            new THREE.Vector3(data.posTo.x, data.posTo.y, 0)
                        );
                        go._moveToObj(targetObj);
                    } else {
                        go._moveTo(data.posTo.x, data.posTo.y);
                    }
                    break;
                case NETOBJCMD.SKILL2SELF:
                    if (go instanceof CharObject && data.skillIdx !== undefined) {
                        go._skillToSelf(data.skillIdx);
                    } else if (go instanceof MobObject && data.npcSkillMotion !== undefined) {
                        go._skillMotion(data.npcSkillMotion);
                    } else {
                        go._stop();
                    }
                    break;
                case NETOBJCMD.SKILL2OBJ:
                    if (data.targetObj && data.skillIdx !== undefined && go instanceof CharObject) {
                        go._skillToObj(
                            GZM.getRefByServerObjectIdx(
                                data.targetObj,
                                new THREE.Vector3(data.posTo.x, data.posTo.y, 0)
                            ),
                            data.skillIdx
                        );
                    } else if (data.targetObj && data.npcSkillMotion !== undefined && go instanceof MobObject) {
                        go._skillMotion(
                            data.npcSkillMotion,
                            GZM.getRefByServerObjectIdx(
                                data.targetObj,
                                new THREE.Vector3(data.posTo.x, data.posTo.y, 0)
                            )
                        );
                    } else {
                        go._stop();
                    }
                    break;
                case NETOBJCMD.SKILL2POS:
                    if (go instanceof CharObject && data.skillIdx !== undefined) {
                        go.setDirection(
                            Math.atan2(
                                data.posTo.y - go.position.y,
                                data.posTo.x - go.position.x
                            ) + Math.PI / 2
                        );
                        go._skillToSelf(data.skillIdx);
                    } else if (go instanceof MobObject && data.npcSkillMotion !== undefined) {
                        go.setDirection(
                            Math.atan2(
                                data.posTo.y - go.position.y,
                                data.posTo.x - go.position.x
                            ) + Math.PI / 2
                        );
                        go._skillMotion(data.npcSkillMotion);
                    } else {
                        go._stop();
                    }
                    break;
            }
        }

        gn.on("spawn_npc", (data: any) => {
            GZM.whenReady((world: any) => {
                // Track this NPC load
                this.pendingNpcLoads++;
                console.log("[NetManager] NPC spawn started, pending:", this.pendingNpcLoads);

                const npc = new NpcObject(world);
                npc.serverObjectIdx = data.objectIdx;
                npc.eventIdx = data.eventIdx;
                npc.dialogIdx = data.dialogIdx;
                npc.stats = new NpcStats(npc, data.charIdx);
                npc.setPosition(data.position.x, data.position.y, 10);
                npc.setDirection((data.modelDir / 180) * Math.PI);
                npc.pawn = new NpcPawn(npc);
                // Start the (mesh-independent) quest-availability check early, but don't apply it
                // until the setChar callback below, once the pawn's mesh/boundingBox is ready —
                // setQuestMarker's height fallback reads boundingBox, which is empty until then.
                const questCheck = computeNpcHasAvailableQuest(npc);

                // Wait for NPC model and resources to load before adding to scene
                npc.setChar(Math.abs(data.charIdx), () => {
                    npc.ingStatus = new IngStatus(
                        npc.pawn.rootObj,
                        data.statusFlags,
                        data.statusTimers
                    );
                    if (data.charIdx < 0) {
                        npc.setVisible(false);
                    }
                    _setCommand(npc, data);
                    for (let i = 0; i < data.eventStatuses.length; ++i) {
                        npc.eventVar[i] = data.eventStatuses[i];
                    }
                    const added = GZM.addObject(npc);
                    if (added) {
                        questCheck.then((hasQuest) => {
                            if (npc.world === GZM.map) npc.pawn?.setQuestMarker(hasQuest);
                        });
                    }

                    // Notify that this NPC has finished loading
                    this._onNpcLoadComplete();
                });
            });
        });

        gn.on("spawn_char", (data: any) => {
            GZM.whenReady((world: any) => {
                const char = new CharObject(world);
                char.serverObjectIdx = data.objectIdx;
                char.name = data.name;
                char.level = data.level;
                char.moveSpeed = data.runSpeed;
                char.setPosition(data.position.x, data.position.y, 10);
                char.gender = data.gender;
                char.hp = Math.max(0, data.hp);
                char.job = data.job;
                char.hairColor = data.hairColor;
                char.visParts = data.parts;
                char.stats = new CharStats(char);
                char.stats.attackSpeed = data.attackSpeed;
                char.stats.attackSpeedBase = data.attackSpeedBase;
                char.pawn = new CharPawn(char);
                if (data.mount?.mounted) {
                    char.isDriving = true;
                    const patItems = data.rideParts.map((part: any) => part.itemNo || 0);
                    // Same race as inventory_data above: a spawn packet for an
                    // already-mounted character can arrive before item_data loads.
                    GDM.get('item_data', (itemData: any) => {
                        const vehicleKind = getPatVehicleKind(itemData.getPatStats(patItems[0])?.subtype);
                        char.pawn.setPatParts(patItems, vehicleKind);
                        char.pawn.setDrivingHeight(true, vehicleKind);
                        char.pawn.playDrivingMotion();
                    });
                }
                char.ingStatus = new IngStatus(
                    char.pawn.rootObj,
                    data.statusFlags,
                    data.statusTimers,
                    data.statusValues
                );
                char.debugValidate();
                _setCommand(char, data);
                GZM.addObject(char);
            });
        });

        gn.on("spawn_mob", (data: any) => {
            // Register before waiting for the map so an obj_remove received
            // during terrain loading can cancel this queued spawn.
            const spawnToken = ++this.nextMobSpawnToken;
            this.pendingMobSpawns.set(data.objectIdx, spawnToken);
            GZM.whenReady((world: any) => {
                // data.charIdx = 2; // 22 // 251; //203;
                // data.objectIdx = 1;

                const spawnMobNow = () => {
                    // Ignore a stale callback after removal or a newer spawn.
                    if (this.pendingMobSpawns.get(data.objectIdx) !== spawnToken) return;
                    this.pendingMobSpawns.delete(data.objectIdx);
                    if (world !== GZM.map) return;
                    if (GZM.findByServerObjectIdx(data.objectIdx)) return;
                    const mob = new MobObject(world);
                    mob.serverObjectIdx = data.objectIdx;
                    mob.stats = new NpcStats(mob, data.charIdx);
                    mob.hp = Math.max(0, data.hp);
                    mob.maxHp = Math.max(mob.hp, data.maxHp || 0);
                    mob.level = data.level;
                    mob.setPosition(data.position.x, data.position.y, 10);
                    mob.moveSpeed = data.moveSpeed || 100;
                    mob.pawn = new NpcPawn(mob);
                    mob.setChar(Math.abs(data.charIdx));
                    mob.ingStatus = new IngStatus(
                        mob.pawn.rootObj,
                        data.statusFlags,
                        data.statusTimers
                    );
                    if (data.charIdx < 0) {
                        mob.setVisible(false);
                    }
                    _setCommand(mob, data);
                    const added = GZM.addObject(mob);
                    const pendingAttack = this.pendingMobAttacks.get(data.objectIdx);
                    this.pendingMobAttacks.delete(data.objectIdx);
                    if (added && pendingAttack && Date.now() - pendingAttack.receivedAt < 2000) {
                        this.playMonsterAttack(mob, pendingAttack.data);
                    }
                };

                // Preload the chunk containing this monster before spawning.
                // Use the MapManager captured when this queued spawn is released;
                // NetManager.world may change again during another transition.
                if (world && world.ensureChunkLoaded) {
                    world.ensureChunkLoaded(
                        data.position.x,
                        data.position.y,
                        spawnMobNow
                    );
                } else {
                    // Fallback: spawn immediately if chunk loading not available
                    spawnMobNow();
                }
            });
        });

        gn.on("dropitem", (data: any) => {
            GZM.whenReady((world: any) => {
                const spawnItemNow = () => {
                    if (world !== GZM.map) return;
                    const item = new ItemObject(world);
                    item.serverObjectIdx = data.objectIdx;
                    item.setPosition(data.position.x, data.position.y, 10);
                    // Unlike ActorObjects, ItemObject has no per-frame gravity
                    // correction, so it must be snapped to terrain height once
                    // here or it stays floating/buried at the placeholder z=10
                    // forever (this is also why it can end up out of raypick
                    // reach and never show the pickup cursor).
                    item.dropFromSky();
                    item.pawn = new ItemPawn(item);
                    item.setItem(data.item);
                    GZM.addObject(item);
                };

                // Preload the chunk containing this drop first, same as
                // spawn_mob above - findHighPoint (used by dropFromSky) returns
                // undefined in an unloaded chunk, which would leave the item at
                // an invalid z.
                if (world && world.ensureChunkLoaded) {
                    world.ensureChunkLoaded(
                        data.position.x,
                        data.position.y,
                        spawnItemNow
                    );
                } else {
                    spawnItemNow();
                }
            });
        });

        gn.on("pickup_item", (data: any) => {
            const obj = GZM.findByServerObjectIdx(data.objectIdx);

            switch (data.result) {
                case REPLY_GET_FIELDITEM_REPLY_OK:
                    if (data.item.itemType === ITEMTYPE.MONEY) {
                        GCM.system(
                            "You have obtained " + data.item.money + " Zulie."
                        );
                    } else {
                        const itemData = GDM.getNow("item_data");
                        const name = itemData.getName(
                            data.item.itemType,
                            data.item.itemNo
                        );

                        if (ITMSTACKABLE[data.item.itemType]) {
                            GCM.system(
                                "You have obtained " +
                                    name +
                                    " (" +
                                    data.item.quantity +
                                    ")"
                            );
                        } else {
                            GCM.system("You have obtained " + name);
                        }
                    }

                    MC.inventory.addItem(data.item);
                    if (obj) {
                        GZM.removeObject(obj);
                    }
                    break;
                case REPLY_GET_FIELDITEM_REPLY_NONE:
                    if (obj) {
                        GZM.removeObject(obj);
                    }
                    break;
                case REPLY_GET_FIELDITEM_REPLY_NO_RIGHT:
                    GCM.system("You have no right to pickup this item!");
                    break;
                case REPLY_GET_FIELDITEM_REPLY_TOO_MANY:
                    GCM.system("You have no space in your inventory!");
                    break;
            }
        });

        gn.on("event_status", (data: any) => {
            const obj = GZM.findByServerObjectIdx(data.objectIdx);
            if (obj instanceof NpcObject) {
                obj.setEventVar(data.id, data.value);
            }
        });

        gn.on("obj_remove", (data: any) => {
            this.pendingMobSpawns.delete(data.objectIdx);
            this.pendingMobAttacks.delete(data.objectIdx);
            const obj = GZM.findByServerObjectIdx(data.objectIdx);
            if (obj) {
                // The lethal damage packet starts the death motion. Its finish
                // callback removes the pawn after the animation has played.
                if (obj instanceof MobObject && !(obj instanceof NpcObject) && obj.isDead) return;
                GZM.removeObject(obj);
            }
        });

        gn.on("obj_moveto", (data: any) => {
            // console.log("[CLIENT] obj_moveto received:", {
            //     objectIdx: data.objectIdx,
            //     posTo: data.posTo,
            //     targetObjectIdx: data.targetObjectIdx,
            // });

            const obj = GZM.findByServerObjectIdx(data.objectIdx);
            if (obj instanceof MyCharacter) {
                // console.log("[CLIENT] Ignoring movement for MyCharacter");
                return;
            }
            if (obj && !(obj instanceof ProxyObject)) {
                // console.log(
                //     "[CLIENT] Moving object:",
                //     obj.constructor.name,
                //     "to",
                //     data.posTo.x,
                //     data.posTo.y
                // );
                let targetObj = null;
                if (data.targetObjectIdx) {
                    targetObj = GZM.getRefByServerObjectIdx(
                        data.targetObjectIdx,
                        new THREE.Vector3(data.posTo.x, data.posTo.y, data.posZ)
                    );
                }
                if (targetObj) {
                    // console.log(
                    //     "[CLIENT] _moveToObj called for",
                    //     obj.constructor.name,
                    //     "id:",
                    //     obj.serverObjectIdx,
                    //     "target:",
                    //     targetObj.constructor.name,
                    //     "id:",
                    //     targetObj.serverObjectIdx
                    // );
                    obj._moveToObj(targetObj);
                } else {
                    // console.log(
                    //     "[CLIENT] _moveTo called for",
                    //     obj.constructor.name,
                    //     "id:",
                    //     obj.serverObjectIdx,
                    //     "pos:",
                    //     data.posTo.x.toFixed(2),
                    //     data.posTo.y.toFixed(2)
                    // );
                    obj._moveTo(data.posTo.x, data.posTo.y);
                }
            } else {
                console.log(
                    "[CLIENT] Object not found or is ProxyObject:",
                    data.objectIdx
                );
            }
        });

        gn.on("obj_attack", (data: any) => {
            const attackerObj = GZM.findByServerObjectIdx(
                data.attackerObjectIdx
            );
            if (attackerObj instanceof MyCharacter) {
                return;
            }
            if (attackerObj && !(attackerObj instanceof ProxyObject)) {
                if (attackerObj instanceof MobObject) {
                    this.playMonsterAttack(attackerObj, data);
                    return;
                }
                const defenderObj = GZM.getRefByServerObjectIdx(
                    data.defenderObjectIdx,
                    new THREE.Vector3(data.posTo.x, data.posTo.y, 0)
                );
                attackerObj._attackObj(defenderObj);
                console.log("obj_attack", data);
            } else if (this.pendingMobSpawns.has(data.attackerObjectIdx)) {
                this.pendingMobAttacks.set(data.attackerObjectIdx, { data, receivedAt: Date.now() });
            }
        });

        gn.on("self_skill", (data: any) => {
            const obj = GZM.findByServerObjectIdx(data.sourceObjectIdx);
            if (obj && !(obj instanceof ProxyObject)) {
                if (obj instanceof CharObject) {
                    obj._skillToSelf(data.skillIdx);
                } else if (obj instanceof MobObject && data.npcSkillMotion !== undefined) {
                    obj._skillMotion(data.npcSkillMotion);
                }
            }
        });

        gn.on("target_skill", (data: any) => {
            const attackerObj = GZM.findByServerObjectIdx(data.sourceObjectIdx);
            if (attackerObj && !(attackerObj instanceof ProxyObject)) {
                if (attackerObj instanceof CharObject) {
                    const defenderObj = GZM.getRefByServerObjectIdx(
                        data.destObjectIdx,
                        new THREE.Vector3(data.posTo.x, data.posTo.y, 0)
                    );
                    attackerObj._skillToObj(defenderObj, data.skillIdx);
                } else if (attackerObj instanceof MobObject && data.npcSkillMotion !== undefined) {
                    attackerObj._skillMotion(
                        data.npcSkillMotion,
                        GZM.getRefByServerObjectIdx(
                            data.destObjectIdx,
                            new THREE.Vector3(data.posTo.x, data.posTo.y, 0)
                        )
                    );
                }
            }
        });

        gn.on("skill_cooldown", (data: any) => {
            MC.skills.setCooldown(data.skillIdx, data.cooldownMs);
        });

        gn.on("item_cooldown", (data: any) => {
            MC.inventory.setCooldown(data.cooldownGroup, data.cooldownMs);
        });

        gn.on("obj_motion", (data: any) => {
            const obj = GZM.findByServerObjectIdx(data.objectIdx);
            if (obj && obj instanceof CharObject) {
                obj._setMotion(data.motionNo, data.loop);
            }
        });

        gn.on("toggle_sit", (data: any) => {
            const obj = GZM.findByServerObjectIdx(data.objectIdx);
            if (obj && obj instanceof CharObject) {
                obj._toggleSit();
            }
        });

        gn.on("damage", (data: any) => {
            const attackerObj = GZM.findByServerObjectIdx(
                data.attackerObjectIdx
            );
            const defenderObj = GZM.findByServerObjectIdx(
                data.defenderObjectIdx
            );
            if (defenderObj && !(defenderObj instanceof ProxyObject)) {
                if (attackerObj === MC || defenderObj === MC) {
                    this.playRegularHitSound(attackerObj);
                }
                if (defenderObj instanceof MobObject && !(defenderObj instanceof NpcObject)) {
                    defenderObj.hp = Math.max(0, defenderObj.hp - data.amount);
                    defenderObj.emit("hp_changed");
                }
                defenderObj.emit("damage", data.amount);

                if (data.flags & 16) {
                    _setDead(defenderObj);
                }
            }
        });

        gn.on("skill_damage", (data: any) => {
            const defenderObj = GZM.findByServerObjectIdx(data.destObjectIdx);
            if (defenderObj && !(defenderObj instanceof ProxyObject)) {
                if (defenderObj instanceof MobObject && !(defenderObj instanceof NpcObject)) {
                    defenderObj.hp = Math.max(0, defenderObj.hp - data.amount);
                    defenderObj.emit("hp_changed");
                }
                defenderObj.emit("damage", data.amount);

                if (data.flags & 16) {
                    _setDead(defenderObj);
                }
            }
        });

        gn.on("skill_effect", (data: any) => {
            const defenderObj = GZM.findByServerObjectIdx(data.destObjectIdx);
            if (defenderObj && !(defenderObj instanceof ProxyObject)) {
                defenderObj.ingStatus.applySkill(
                    data.skillIdx,
                    data.successBits,
                    data.primaryStat,
                    data.secondaryStat
                );
            }
        });

        // Keyed by `${targetObjectIdx}:${statusEffectId}` so a recast before the
        // previous cast's duration elapses removes the stale effect instead of
        // stacking a second, overlapping one. Mirrors BuffData's same pattern for
        // the buff-icon bar (keyed by statusEffectId, identity-checked timers).
        const activeBuffEffects = new Map<string, { remove: () => void }>();

        gn.on("buff_applied", (data: any) => {
            const targetObj = GZM.findByServerObjectIdx(data.targetObjectIdx);
            if (!targetObj || targetObj instanceof ProxyObject) return;

            const statusData = GDM.getNow('list_status');
            const statusRow = statusData.row(data.statusEffectId);
            const effectFileIdx = Number(statusRow[10]) || 0;

            const key = `${data.targetObjectIdx}:${data.statusEffectId}`;
            const existing = activeBuffEffects.get(key);
            if (existing) {
                existing.remove();
            }

            const handle = attachBuffEffect(targetObj.pawn, effectFileIdx);
            activeBuffEffects.set(key, handle);

            setTimeout(() => {
                if (activeBuffEffects.get(key) === handle) {
                    activeBuffEffects.delete(key);
                }
                handle.remove();
            }, data.durationMs);

            if (targetObj === MC) {
                MC.buffs.applyBuff(data.skillIdx, data.statusEffectId, data.durationMs);
            }
        });

        gn.on("char_hpmp_info", (data: any) => {
            MC.hp = Math.max(0, data.curHp);
            MC.mp = data.curMp;
            MC.recoveryHp = data.recoveryTickHp;
            MC.recoveryMp = data.recoveryTickMp;
            MC.changed();
        });

        gn.on("weight_update", (data: any) => {
            if (MC) {
                MC.weightRate = data.weightRate;
                MC.changed();
            }
        });

        gn.on("set_xp", (data: any) => {
            const fromObj = GZM.findByServerObjectIdx(data.fromObjectIdx);

            GCM.system(
                "You have earned " + (data.xp - MC.xp) + " experience points."
            );
            MC.xp = data.xp;
            MC.stamina = data.stamina;
            MC.changed();

            if (fromObj && !(fromObj instanceof ProxyObject)) {
                GCM.system("You have defeated " + fromObj.name + ".");
            }
        });

        gn.on("base_stats_update", (data: any) => {
            if (data.objectIdx !== MC.serverObjectIdx) {
                return;
            }

            Object.assign(MC.stats, data.stats);
            MC.statPoints = data.statPoints;
            MC.changed();
        });

        gn.on("derived_stats_update", (data: any) => {
            if (data.objectIdx !== MC.serverObjectIdx) {
                return;
            }

            MC.hp = Math.max(0, data.hp);
            MC.mp = data.mp;
            Object.assign(MC.calculatedStats, {
                maxHp: data.maxHp,
                maxMp: data.maxMp,
                attackPower: data.attackPower,
                defense: data.defense,
                magicDefense: data.magicDefense,
                accuracy: data.accuracy,
                dodge: data.dodge,
                critical: data.critical,
                attackSpeed: data.attackSpeed,
                moveSpeed: data.moveSpeed,
                maxWeight: data.maxWeight,
                maxSummonGauge: data.maxSummonGauge,
                mpReduction: data.mpReduction,
                bonusStats: data.bonusStats,
            });
            MC.moveSpeed = data.moveSpeed;
            MC.changed();
        });

        gn.on("level_up", (data: any) => {
            const obj = GZM.findByServerObjectIdx(data.objectIdx);

            if (obj instanceof MyCharacter && data.level) {
                GCM.system("You are now level " + data.level + "!");
                MC.level = data.level;
                MC.xp = data.xp;
                MC.statPoints = data.statPoints;
                MC.skillPoints = data.skillPoints;
                MC.changed();
            } else {
                // Play level up effect on obj!
            }
        });

        gn.on("player_died", (data: any) => {
            const obj = GZM.findByServerObjectIdx(data.objectIdx);
            if (!(obj instanceof MyCharacter)) {
                return;
            }

            _setDead(obj);

            GCM.system("You have died.");

            // Delay the dialog itself by the server's respawnDelay (must
            // match RespawnSystem.DEFAULT_CONFIG.respawnDelay,
            // server/src/world-server/RespawnSystem.ts) rather than showing
            // it immediately with a "Respawn" button that silently no-ops --
            // clicking it during the delay used to just replay the server's
            // "please wait" chat message and close the dialog with no way to
            // retry. Showing it only once the delay has elapsed guarantees
            // the click always succeeds.
            // ponytail: no location-choice UI, single default respawn point.
            setTimeout(() => {
                ui.messageBox("You can respawn now.", [
                    "Respawn",
                ]).on("respawn", () => {
                    netGame.requestRespawn();
                });
            }, 5000);
        });

        gn.on("player_respawned", (data: any) => {
            const obj = GZM.findByServerObjectIdx(data.objectIdx);
            if (!(obj instanceof MyCharacter)) return;

            // A server relocation invalidates every locally predicted command.
            // Cancel synchronously so an old click destination cannot resume on
            // the new map before the landing position is applied.
            obj._cancelCommands();

            const applyRevivedPosition = () => {
                const terrainZ = this.world && this.world.findHighPoint
                    ? this.world.findHighPoint(data.x, data.y)
                    : undefined;
                obj.setPosition(
                    data.x,
                    data.y,
                    terrainZ !== undefined ? terrainZ : data.z / 100
                );
                obj._revive();
                obj.changed();
            };

            if (data.mapId !== obj.zoneNo) {
                GZM.transitionTo(
                    data.mapId,
                    { x: data.x, y: data.y, z: data.z / 100 },
                    () => {
                        obj.zoneNo = data.mapId;
                        this.world = GZM.map;
                        if (!this.verifyMapRevision(data.mapRevision)) return;
                        applyRevivedPosition();
                    }
                );
                // transitionTo installs the new MapManager synchronously. Point
                // packet handlers at it now; zone-bound spawns wait on GZM.whenReady.
                this.world = GZM.map;
            } else {
                obj.zoneNo = data.mapId;
                if (!this.verifyMapRevision(data.mapRevision)) return;
                applyRevivedPosition();
            }
        });

        gn.on("player_teleported", (data: any) => {
            const obj = GZM.findByServerObjectIdx(data.objectIdx);
            if (!(obj instanceof MyCharacter)) return;

            // This also covers same-zone teleports. Leaving the old command now
            // clears its click marker and prevents later reached-position syncs
            // from reporting movement toward a pre-teleport destination.
            obj._cancelCommands();

            const applyPosition = () => {
                const terrainZ = this.world && this.world.findHighPoint
                    ? this.world.findHighPoint(data.x, data.y)
                    : undefined;
                obj.setPosition(
                    data.x,
                    data.y,
                    terrainZ !== undefined ? terrainZ : data.z / 100
                );
                obj.changed();
            };

            if (data.mapId !== obj.zoneNo) {
                GZM.transitionTo(
                    data.mapId,
                    { x: data.x, y: data.y, z: data.z / 100 },
                    () => {
                        obj.zoneNo = data.mapId;
                        this.world = GZM.map;
                        if (!this.verifyMapRevision(data.mapRevision)) return;
                        applyPosition();
                    }
                );
                // Destination snapshot packets follow the arrival packet and can
                // be received before terrain loading finishes.
                this.world = GZM.map;
            } else {
                obj.zoneNo = data.mapId;
                if (!this.verifyMapRevision(data.mapRevision)) return;
                applyPosition();
            }
        });

        gn.on("chat_say", (data: any) => {
            const senderObj = GZM.findByServerObjectIdx(data.senderObjectIdx);
            if (senderObj && senderObj instanceof CharObject) {
                GCM.addGameMessage(
                    MSG_TYPE.SAY,
                    data.message,
                    senderObj.name,
                    senderObj
                );
                // Show chat bubble
                if (senderObj.pawn && senderObj.pawn.showChatBubble) {
                    senderObj.pawn.showChatBubble(data.message);
                }
            }
        });

        gn.on("chat_shout", (data: any) => {
            GCM.addGameMessage(MSG_TYPE.SHOUT, data.message, data.senderName, null);
        });

        gn.on("chat_whisper", (data: any) => {
            GCM.addGameMessage(MSG_TYPE.WHISPER, data.message, data.senderName, null);
        });

        gn.on("chat_party", (data: any) => {
            GCM.addGameMessage(MSG_TYPE.PARTY, data.message, data.senderName, null);
        });

        gn.on("chat_trade", (data: any) => {
            GCM.addGameMessage(MSG_TYPE.TRADE, data.message, data.senderName, null);
        });

        gn.on("chat_clan", (data: any) => {
            GCM.addGameMessage(MSG_TYPE.CLAN, data.message, data.senderName, null);
        });

        gn.on("chat_ally", (data: any) => {
            GCM.addGameMessage(MSG_TYPE.ALLY, data.message, data.senderName, null);
        });

        gn.on("quest_reply", (data: any) => {
            switch (data.result) {
                case RESULT_QUEST_REPLY_DEL_SUCCESS:
                    MC.quests.removeQuestAt(data.questSlot);
                    break;
                case RESULT_QUEST_REPLY_TRIGGER_SUCCESS:
                case RESULT_QUEST_REPLY_TRIGGER_FAILED:
                    // Quest log/var updates for a successful trigger arrive via
                    // their own packets (pakQuestVars/pakQuestLog); nothing else
                    // to apply from this reply itself.
                    break;
                case RESULT_QUEST_REPLY_ADD_SUCCESS:
                case RESULT_QUEST_REPLY_ADD_FAILED:
                case RESULT_QUEST_REPLY_DEL_FAILED:
                case RESULT_QUEST_REPLY_UPDATE:
                case RESULT_QUEST_REPLY_COMPLETE:
                case RESULT_QUEST_REPLY_RESET:
                case RESULT_QUEST_REPLY_DAILY_RESET:
                default:
                    console.warn(
                        "Unimplemented quest reply result " + data.result
                    );
            }
        });

        gn.on("set_hot_icon", (data: any) => {
            MC.hotIcons.setIcon(data.id, data.type, data.slot);
        });

        gn.on("char_equip_item", (data: any) => {
            // data.equipIdx is a raw items[] array index: < INVEQUIPIDX.MAX
            // is an equip slot, anything else is a backpack slot. This
            // packet only carries itemnum/gem/refine (no itemType), so it
            // can't reliably tell us which physical item moved where -- the
            // 3D avatar visual below is all it's good for. Authoritative
            // inventory/equip state comes from the pakInventory snapshot
            // the server sends right after (see pakChangeEquip), which
            // fires MC.inventory's own "changed" event via setItems().
            if (data.equipIdx >= INVEQUIPIDX.MAX) return;

            const obj = GZM.findByServerObjectIdx(data.objectIdx);
            if (obj instanceof CharObject) {
                const pawn = obj.pawn as any;
                const index = ITMPARTTOVISPART[data.equipIdx];

                if (index !== undefined) {
                    obj.visParts[index] = data.partItem;

                    if (pawn instanceof CharPawn) {
                        // @ts-ignore - pawn is CharPawn instance
                        pawn.setModelPart(
                            index,
                            data.partItem.itemNo,
                            data.partItem.refineGrade || 0,
                            undefined,
                            data.partItem.gemOption1 || 0
                        );
                        if (index === VISEQUIPIDX.WEAPON) {
                            // Weapon change swaps weaponMotionType -- replay
                            // idle so the new pose/animation set shows immediately.
                            // @ts-ignore - pawn is CharPawn instance
                            pawn.playIdleMotion();
                        }
                    }
                }
            }
        });

        gn.on("toggle_drive", (data: any) => {
            const obj = GZM.findByServerObjectIdx(data.objectIdx);
            if (!(obj instanceof CharObject)) return;
            obj.isDriving = data.stance === 4;
            if (data.runSpeedBase !== undefined) {
                obj.moveSpeed = data.runSpeedBase;
            }
            if (obj === MC) {
                if (MC.calculatedStats && data.runSpeedBase !== undefined) {
                    MC.calculatedStats.moveSpeed = data.runSpeedBase;
                }
                MC.changed();
            }
            const pawn = obj.pawn as any;
            if (!(pawn instanceof CharPawn)) return;
            const patItems = data.patItems || (obj === MC && MC.inventory
                ? [135, 136, 137, 138, 139].map((slot) => MC.inventory.findBySlot(slot)?.itemNo || 0)
                : []);
            const vehicleKind = obj.isDriving && patItems[0]
                ? getPatVehicleKind(GDM.getNow('item_data').getPatStats(patItems[0])?.subtype)
                : null;
            pawn.setDrivingHeight(obj.isDriving, vehicleKind);

            // gameclient.ts's 0x782 handler emits this same event for both
            // Stance===4 (entering DRIVING) and Stance===3 (RUNNING -- which
            // also covers "stood up from sitting", not just "stopped
            // driving"). Only actually attach/detach parts when the pawn
            // has something to show for it: entering always does, exiting
            // is a harmless no-op via clearPatParts() when nothing was
            // attached (e.g. a plain stand-from-sit never called
            // setPatParts in the first place).
            if (data.stance !== 4) {
                pawn.clearPatParts();
                pawn.playIdleMotion();
                return;
            }

            // Remote characters use the PAT item numbers in the stance
            // broadcast; MC can also read its own inventory snapshot.
            if (patItems.length) {
                pawn.setPatParts(patItems, vehicleKind);
            }
            pawn.playDrivingMotion();
        });

        gn.on("party_req", (data: any) => {
            const obj = GZM.findByServerObjectIdx(data.objectIdx);

            if (!(obj instanceof CharObject)) {
                console.warn("Invited to party by unknown objectIdx");
                return;
            }

            switch (data.request) {
                case PARTY_REQ_MAKE:
                case PARTY_REQ_JOIN:
                    ui.messageBox(obj.name + " has invited you to party", [
                        "Accept",
                        "Decline",
                    ])
                        .on("accept", () => {
                            netGame.partyReply(
                                PARTY_REPLY_ACCEPT_MAKE,
                                data.objectIdx
                            );
                        })
                        .on("decline", () => {
                            netGame.partyReply(
                                PARTY_REPLY_REJECT_JOIN,
                                data.objectIdx
                            );
                        });
                    break;
            }
        });

        gn.on("party_reply", (data: any) => {
            const obj = GZM.findByServerObjectIdx(data.objectIdx);
            let name = "{Unknown}";

            if (obj instanceof CharObject) {
                name = obj.name;
            }

            switch (data.reply) {
                case PARTY_REPLY_ACCEPT_MAKE:
                    MC.party.setLeaderByIdx(MC.serverObjectIdx);
                    break;
                case PARTY_REPLY_NOT_FOUND:
                    ui.messageBox("Party not found");
                    break;
                case PARTY_REPLY_BUSY:
                    ui.messageBox(
                        "Could not invite " +
                            name +
                            " to party, target is busy."
                    );
                    break;
                case PARTY_REPLY_REJECT_JOIN:
                    ui.messageBox(
                        "Could not invite " +
                            name +
                            " to party, target rejected invite."
                    );
                    break;
                case PARTY_REPLY_DESTROY:
                    MC.party.clear();
                    break;
                case PARTY_REPLY_FULL_MEMBERS:
                    ui.messageBox(
                        "Could not invite " + name + " to party, party is full."
                    );
                    break;
                case PARTY_REPLY_INVALID_LEVEL:
                    ui.messageBox(
                        "Could not invite " +
                            name +
                            " to party, level requirements not met."
                    );
                    break;
                case PARTY_REPLY_CHANGE_OWNER:
                    MC.party.setLeaderByIdx(data.objectIdx);
                    break;
                case PARTY_REPLY_CHANGE_OWNERnDISCONN:
                    MC.party.disconnectMember(MC.party.leaderTag);
                    MC.party.setLeaderByIdx(data.objectIdx);
                    break;
                case PARTY_REPLY_NO_CHARGE_TARGET:
                    ui.messageBox("Cannot charge that target.");
                    break;
                case PARTY_REPLY_DISCONNECT:
                    MC.party.disconnectMember(data.objectTag);
                    break;
                case PARTY_REPLY_BAN:
                    MC.party.kickMember(data.objectTag);
                    break;
                case PARTY_REPLY_SHARE_ENABLED:
                    GCM.system("Party experience sharing is enabled.");
                    break;
                case PARTY_REPLY_SHARE_DISABLED:
                    GCM.system("Party experience sharing is disabled.");
                    break;
            }
        });

        gn.on("party_member", (data: any) => {
            if (MC.party.exists) {
                if (data.leaverTag) {
                    MC.party.leaveMember(data.leaverTag);

                    if (data.leaverTag === MC.party.leaderTag) {
                        const member = MC.party.findMemberByTag(
                            data.newLeaderTag
                        );
                        MC.party.setLeader(member.serverIdx, member.serverTag);
                    }
                } else {
                    MC.party.addMembers(data.members);
                }
            } else {
                MC.party.create();
                MC.party.setRule(data.rule);
                MC.party.addMembers(data.members);

                if (MC.leaderTag !== MC.uniqueTag) {
                    MC.party.setLeaderByIdx(data.members[0].serverIdx);
                }
            }
        });

        gn.on("party_member_update", (data: any) => {
            MC.party.updateMember(data.member.serverTag, data.member);
        });

        gn.on("party_xp", (data: any) => {
            MC.party.setLevelXP(data.level, data.xp);

            if (data.isLevelUp) {
                MC.party.levelup();
            }
        });

        gn.on("party_rule", (data: any) => {
            MC.party.setRule(data.rule);
        });

        gn.on("trade_req", (data: any) => {
            ui.messageBox(data.name + " wants to trade with you", [
                "Accept",
                "Decline",
            ])
                .on("accept", () => netGame.tradeResponse(true))
                .on("decline", () => netGame.tradeResponse(false));
        });

        gn.on("trade_start", (data: any) => {
            if (data.accepted) {
                MC.trade.open(data.name);
            }
        });

        gn.on("trade_item", (data: any) => {
            const isMine = data.senderIdx === MC.serverObjectIdx;

            if (data.isAdd) {
                if (isMine) {
                    MC.trade.addMyItem(data.slotNum, data.item);
                } else {
                    MC.trade.addTheirItem(data.slotNum, data.item);
                }
            } else {
                if (isMine) {
                    MC.trade.removeMyItem(data.slotNum);
                } else {
                    MC.trade.removeTheirItem(data.slotNum);
                }
            }
        });

        gn.on("trade_zuly", (data: any) => {
            if (data.senderIdx === MC.serverObjectIdx) {
                MC.trade.setMyZuly(data.amount);
            } else {
                MC.trade.setTheirZuly(data.amount);
            }
        });

        gn.on("trade_confirm", (data: any) => {
            if (data.confirmerIdx === MC.serverObjectIdx) {
                MC.trade.setMyConfirmed(data.confirmed);
            } else {
                MC.trade.setTheirConfirmed(data.confirmed);
            }
        });

        gn.on("trade_complete", () => {
            GCM.system("Trade completed.");
            MC.trade.clear();
        });

        gn.on("trade_cancel", () => {
            GCM.system("Trade cancelled.");
            MC.trade.clear();
        });

        gn.on("vending_open_notify", (data: any) => {
            this.vendingPropIds.set(data.ownerClientId, data.propObjectIdx);
            const ownerObj = GZM.findByServerObjectIdx(data.ownerClientId);
            if (ownerObj && !(ownerObj instanceof ProxyObject)) {
                ownerObj.isVending = true;
                ownerObj.setVisible(false);
            }

            const prop = new NpcObject(this.world);
            prop.serverObjectIdx = data.propObjectIdx;
            prop.isVendingProp = true;
            prop.vendingOwnerObjectIdx = data.ownerClientId;
            prop.name = data.title;
            prop.setPosition(data.x, data.y, 10);
            prop.pawn = new ItemPawn(prop);
            prop.pawn.playIdleMotion = () => {};
            GDM.get("fielditem_models", (models: any) => {
                if (this.vendingPropIds.get(data.ownerClientId) !== data.propObjectIdx) return;
                const meshIdx = models.data.meshes.findIndex((path: string) =>
                    path.replace(/\\/g, "/").toLowerCase().endsWith("/di41/store.zms")
                );
                const modelIdx = models.data.models.findIndex((model: any) =>
                    model?.parts.some((part: any) => part.meshIdx === meshIdx)
                );
                if (modelIdx < 0) {
                    console.warn("Vending store model not found");
                    if (ownerObj && !(ownerObj instanceof ProxyObject)) ownerObj.setVisible(true);
                    return;
                }
                // The ZSC uses mixed-case Windows paths while the mounted
                // converted assets have uppercase path components.
                models.data.meshes[meshIdx] = "3DDATA/ITEM/DI41/STORE.ZMS";
                for (const part of models.data.models[modelIdx].parts) {
                    models.data.materials[part.materialIdx].texturePath = "3DDATA/ITEM/DI41/STORE.DDS";
                }
                prop.pawn._setModel(models, modelIdx, () => {
                    if (this.vendingPropIds.get(data.ownerClientId) === data.propObjectIdx) {
                        GZM.addObject(prop);
                    }
                });
            });
        });

        gn.on("vending_close_notify", (data: any) => {
            this.vendingPropIds.delete(data.ownerClientId);
            const ownerObj = GZM.findByServerObjectIdx(data.ownerClientId);
            if (ownerObj && !(ownerObj instanceof ProxyObject)) {
                ownerObj.isVending = false;
                ownerObj.setVisible(true);
            }
            if (MC.vending.browsing && MC.vending.browsing.ownerClientId === data.ownerClientId) {
                MC.vending.clearBrowsing();
            }
        });

        gn.on("vending_open_ack", (data: any) => {
            if (data.success) {
                MC.vending.confirmOpen(data.title);
                GCM.system("Your shop is now open.");
            }
        });

        gn.on("vending_close_ack", () => {
            MC.vending.close();
        });

        gn.on("vending_sell_slot", (data: any) => {
            if (data.quantity === 0) {
                MC.vending.removeMySellItem(data.slotNum);
            }
            // A quantity > 0 ack just confirms what setMySellItem already
            // set locally when the item was dropped — nothing further to do.
        });

        gn.on("vending_buy_slot", (data: any) => {
            if (data.desiredQty === 0) {
                MC.vending.removeMyBuyItem(data.slotNum);
            }
        });

        gn.on("vending_contents", (data: any) => {
            const isPending = data.ownerClientId === MC.vending.pendingBrowseOwnerId;
            const isRefreshOfCurrent = MC.vending.browsing && data.ownerClientId === MC.vending.browsing.ownerClientId;
            if (!isPending && !isRefreshOfCurrent) {
                return; // Not who this client asked to browse — a broadcast to a bystander, ignore it.
            }
            MC.vending.pendingBrowseOwnerId = null;

            const sellItems = new Map();
            for (const e of data.sellItems) {
                sellItems.set(e.slot, e);
            }
            const buyItems = new Map();
            for (const e of data.buyItems) {
                buyItems.set(e.slot, e);
            }

            const ownerObj = GZM.findByServerObjectIdx(data.ownerClientId);
            MC.vending.setBrowsing({
                ownerClientId: data.ownerClientId,
                ownerName: ownerObj ? ownerObj.name : '',
                sellItems,
                buyItems,
                title: data.title,
            });
        });

        gn.on("party_item", function (data: any) {
            const member = MC.party.findMemberByIdx(data.objectIdx);

            if (member) {
                if (data.item.itemType === ITEMTYPE.MONEY) {
                    GCM.system(
                        member.name + " obtained " + data.item.money + " Zulie."
                    );
                } else {
                    const itemData = GDM.getNow("item_data");
                    const name = itemData.getName(
                        data.item.itemType,
                        data.item.itemNo
                    );

                    if (ITMSTACKABLE[data.item.itemType]) {
                        GCM.system(
                            member.name +
                                " obtained " +
                                name +
                                " (" +
                                data.item.quantity +
                                ")"
                        );
                    } else {
                        GCM.system(member.name + " obtained " + name);
                    }
                }
            }
        });
    }
}

/**
 * @type {_NetManager}
 */
export const NetManager = new _NetManager();
